import { useEffect, useState } from 'react';
import { useWorkspace } from '@trello/workspaces';
import { usePreviousWhileLoading } from 'app/src/components/WorkspaceNavigation/usePreviousWhileLoading';
import { useBoardBackgroundQuery } from './BoardBackgroundQuery.generated';
import type { BoardPrefs } from './BoardTheme.types';
import { getBrightness } from '@trello/a11y';

export interface BoardBackground
  extends Pick<NonNullable<BoardPrefs>, 'backgroundBrightness'> {
  backgroundColor: string | undefined;
  backgroundTopColor: string;
  isBackgroundImage: boolean;
}

const emptyBoardBackground: BoardBackground = {
  backgroundBrightness: 'unknown',
  backgroundColor: undefined,
  backgroundTopColor: '',
  isBackgroundImage: false,
};

/**
 * Given a board prefs object with background properties defined, get the
 * BoardBackground model in return. Extracted from `useBoardBackground` for
 * usage outside of React.
 */
export const getBoardBackground = (prefs: BoardPrefs): BoardBackground => {
  if (!prefs) {
    return emptyBoardBackground;
  }
  const {
    background,
    backgroundColor,
    backgroundTopColor,
    backgroundBrightness,
    backgroundImage,
  } = prefs;
  const isGradient = !!background?.includes('gradient');

  let backgroundBrightnessComputed = backgroundBrightness;
  if (
    backgroundTopColor &&
    backgroundImage &&
    backgroundBrightness === 'unknown'
  ) {
    // It is possible for backgroundBrightness to be "unknown" in cases where the
    // image preview service times out or fails. Calculate it now.
    backgroundBrightnessComputed = getBrightness(backgroundTopColor);
  } else if (backgroundImage && !backgroundTopColor) {
    // If there is an image background, we need backgroundTopColor to compute
    // the color for nav. If missing, default to empty so text in nav is readable.
    return emptyBoardBackground;
  }
  return {
    backgroundColor: backgroundColor ?? undefined,
    backgroundTopColor,
    backgroundBrightness: backgroundBrightnessComputed,
    isBackgroundImage: Boolean(backgroundImage && !isGradient),
  };
};

/**
 * Given a board ID, derive the values required to hydrate a usable
 * BoardBackground model. To get these values for the current board, refer to
 * `useCurrentBoardBackground` instead.
 */
export function useBoardBackground({
  idBoard,
  waitOn = ['None'],
}: {
  idBoard: string | null;
  waitOn?: Parameters<typeof useBoardBackgroundQuery>[0]['waitOn'];
}) {
  const skip = !idBoard;

  // `/1/board/{idBoard}?fields=id,prefs&operationName=BoardBackground`
  const { data } = useBoardBackgroundQuery({
    skip,
    variables: { boardId: idBoard || '' },
    waitOn,
  });

  const [boardBackground, setBoardBackground] = useState(emptyBoardBackground);

  useEffect(() => {
    setBoardBackground(getBoardBackground(data?.board?.prefs));
  }, [data?.board?.prefs]);

  return boardBackground;
}

export function useCurrentBoardBackground() {
  const workspace = useWorkspace();
  const idBoard = usePreviousWhileLoading(
    workspace.idBoard,
    workspace.isLoading,
    null,
  );
  return useBoardBackground({ idBoard, waitOn: ['CurrentBoardFull'] });
}
